import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Stack, SimpleGrid, Card, Group, Text, Title, Badge, Button } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState, PageHeader, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { admin } from '@repletoryreach/sdk-api';
import { formatInteger, formatRelative, formatDateTime } from '../../lib/format';
import type { Indexed } from '../../lib/types';
import { StatCard } from '../../components/StatCard';
import { TenantName } from '../../components/TenantName';
import { TwoLineCell } from '../../components/TwoLineCell';
import { QUALITY_RATINGS, countByRating, normaliseRating, qualityTone } from './lib/quality';
import { hasOpenNameRequest } from './lib/displayName';
import { useT } from '../../i18n/I18nProvider';

// The service name the health endpoint reports for the WhatsApp channel.
const CHANNEL_SERVICE = 'channel-wa';

// How many of the worst-rated numbers the hub previews before handing over to
// the full inventory. Enough to see a pattern, few enough not to be the list.
const PREVIEW_ROWS = 5;

/** Sort order for the preview: the ratings that need action, worst first. */
const RANK: Record<string, number> = { RED: 0, LOW: 1, YELLOW: 2 };

type PhoneNumber = admin.AdminPhoneNumber;

/** 18px line icons for the stat tiles. The tint comes from the card's tone. */
const icon = (path: ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {path}
  </svg>
);
const PhoneIcon = icon(<path d="M6.2 3.5 8 3.8l1 3-1.5 1.2a9 9 0 0 0 3.5 3.5L12.2 10l3 1 .3 1.8a1.5 1.5 0 0 1-1.6 1.7A11 11 0 0 1 4.5 5.1 1.5 1.5 0 0 1 6.2 3.5z" />);
const ActiveIcon = icon(<><circle cx="10" cy="10" r="6.8" /><path d="M7 10.2 9 12.2l4-4.4" /></>);
const AlertIcon = icon(<><path d="M10 3.5 17 16H3z" /><path d="M10 8v3.4M10 13.6v.1" /></>);

/**
 * The hub for the messaging channels: the shape of the number inventory, the
 * health of the WhatsApp channel service, and the two surfaces behind it.
 *
 * Everything here is computed from the same cached `['admin','numbers']` query
 * the inventory page uses, so the hub costs one request rather than two and
 * the two pages can never disagree about the counts.
 */
export default function GatewaysPage() {
  const t = useT();
  const navigate = useNavigate();

  const numbersQ = useQuery({
    queryKey: ['admin', 'numbers'],
    queryFn: () => admin.listPhoneNumbers()
  });

  const numbers = useMemo(() => (numbersQ.data?.data ?? []) as PhoneNumber[], [numbersQ.data]);
  const loaded = Boolean(numbersQ.data) && !numbersQ.error;
  const numberCount = loaded ? numbers.length : undefined;
  const activeNumbers = loaded ? numbers.filter((n) => n.is_active).length : undefined;
  const tenantCount = loaded ? new Set(numbers.map((n) => n.organisation_id)).size : undefined;
  const ratings = countByRating(numbers);
  const nameRequests = numbers.filter(hasOpenNameRequest).length;
  const ownAccounts = numbers.filter((n) => n.meta_billing_payer === 'customer').length;

  // Worst first, so the row an operator has to act on is the first one they
  // read. Numbers Meta has not rated are left to the inventory page: an
  // unrated number is an unknown, not a problem.
  const attention = useMemo(
    () => numbers
      .filter((n) => {
        const rating = normaliseRating(n.quality_rating);
        return rating !== null && rating !== 'OTHER' && rating !== 'GREEN';
      })
      .sort((a, b) => (RANK[String(normaliseRating(a.quality_rating))] ?? 3) - (RANK[String(normaliseRating(b.quality_rating))] ?? 3))
      .slice(0, PREVIEW_ROWS),
    [numbers]
  );

  const healthQ = useQuery({
    queryKey: ['admin', 'ops', 'health'],
    queryFn: () => admin.listServiceHealth(),
    staleTime: 30_000
  });
  const channelHealth = healthQ.data?.data?.find((r) => r.service === CHANNEL_SERVICE);

  const share = (n: number | undefined) =>
    n === undefined || !numberCount ? undefined : t('gateways.metrics.shareOfAttached', { pct: ((n / numberCount) * 100).toFixed(1) });

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.section.messaging')}
        title={<>{t('gateways.title')} <Badge size="sm" variant="light" color="gray">{t('gateways.overviewBadge')}</Badge></>}
        description={t('gateways.description')}
        actions={
          // The service's own health, beside the counts it produces, because
          // "no numbers are sending" and "the channel is down" are the same
          // incident read from two ends.
          <Card withBorder>
            <Group gap="md" align="center" wrap="nowrap">
              <Stack gap={2}>
                <Text role="caption" c="var(--rr-color-text-muted)">{t('gateways.health.title')}</Text>
                {channelHealth
                  ? (
                    <StatusBadge status={channelHealth.status === 'healthy' ? 'healthy' : channelHealth.status === 'down' ? 'down' : 'warning'}>
                      {channelHealth.status}
                    </StatusBadge>
                  )
                  : (
                    <Tooltip label={t('gateways.health.unknownHint')} position="top">
                      <span><StatusBadge status="inactive">{t('gateways.health.unknown')}</StatusBadge></span>
                    </Tooltip>
                  )}
              </Stack>
              <Button size="xs" variant="outline" onClick={() => navigate('/system/health')}>
                {t('gateways.health.viewAll')}
              </Button>
            </Group>
          </Card>
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <StatCard
          loading={numbersQ.isLoading}
          tone="primary"
          icon={PhoneIcon}
          label={t('gateways.metrics.attached')}
          value={numberCount === undefined ? t('common.notAvailable') : formatInteger(numberCount)}
          hint={tenantCount === undefined ? undefined : t('gateways.metrics.acrossTenants', { count: formatInteger(tenantCount) })}
        />
        <StatCard
          loading={numbersQ.isLoading}
          tone="success"
          icon={ActiveIcon}
          label={t('gateways.metrics.active')}
          value={activeNumbers === undefined ? t('common.notAvailable') : formatInteger(activeNumbers)}
          hint={share(activeNumbers)}
        />
        <StatCard
          loading={numbersQ.isLoading}
          tone="error"
          icon={AlertIcon}
          label={t('gateways.metrics.red')}
          value={loaded ? formatInteger(ratings.RED) : t('common.notAvailable')}
          hint={loaded ? (share(ratings.RED) ?? t('gateways.metrics.redHint')) : undefined}
          to="/gateways/numbers"
          linkLabel={t('gateways.preview.viewAll')}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <ChannelCard
          title={t('gateways.cards.numbers.title')}
          description={t('gateways.cards.numbers.description')}
          workstream="W2+" // i18n-ignore: internal workstream code
          footer={
            numbersQ.error ? (
              <Text role="caption" c="var(--rr-color-error)">{t('gateways.cards.numbers.loadError')}</Text>
            ) : numberCount === undefined ? (
              <Text role="caption" c="var(--rr-color-text-muted)">{t('common.loading')}</Text>
            ) : (
              <Stack gap="xs">
                <Text role="caption" c="var(--rr-color-text-muted)">
                  {t('gateways.cards.numbers.attached', { count: numberCount })}
                  {activeNumbers !== undefined ? ` · ${t('gateways.cards.numbers.active', { count: activeNumbers })}` : ''}
                </Text>
                <Group gap="xs" wrap="wrap">
                  {QUALITY_RATINGS.filter((r) => ratings[r] > 0).map((r) => (
                    <StatusBadge key={r} status={qualityTone(r)} size="xs">{`${r} ${ratings[r]}`}</StatusBadge>
                  ))}
                  {ratings.unrated > 0 && (
                    <StatusBadge status="inactive" size="xs">{`${t('gateways.numbers.kpi.unrated')} ${ratings.unrated}`}</StatusBadge>
                  )}
                  {/* Businesses waiting on the owner to rename a number in WhatsApp Manager. */}
                  {nameRequests > 0 && (
                    <StatusBadge status="warning" size="xs">{t('gateways.cards.numbers.nameRequests', { count: formatInteger(nameRequests) })}</StatusBadge>
                  )}
                  {ownAccounts > 0 && (
                    <StatusBadge status="inactive" size="xs">{t('gateways.cards.numbers.ownAccounts', { count: formatInteger(ownAccounts) })}</StatusBadge>
                  )}
                </Group>
              </Stack>
            )
          }
          actions={
            <Group gap="xs">
              {Boolean(numbersQ.error) && (
                <Button size="xs" variant="outline" loading={numbersQ.isFetching} onClick={() => void numbersQ.refetch()}>{t('action.retry')}</Button>
              )}
              <Button size="xs" onClick={() => navigate('/gateways/numbers')}>{t('gateways.cards.numbers.openNumbers')}</Button>
            </Group>
          }
        />

        <ChannelCard
          title={t('gateways.cards.wba.title')}
          description={t('gateways.cards.wba.description')}
          workstream="W22" // i18n-ignore: internal workstream code
          // The card used to read like a live metric beside a live one, so
          // operators clicked Open expecting a list of tenants awaiting
          // approval. It now says plainly that the feed is not built.
          footer={
            <Alert color="warning" variant="light">{t('gateways.cards.wba.countLabel')}</Alert>
          }
          actions={
            <Tooltip label={t('gateways.cards.wba.openHint')} position="top">
              <span>
                <Button size="xs" variant="outline" onClick={() => navigate('/gateways/wba')}>{t('action.open')}</Button>
              </span>
            </Tooltip>
          }
        />
      </SimpleGrid>

      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Stack gap={2}>
              <Title order={4}>{t('gateways.preview.title')}</Title>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('gateways.preview.description')}</Text>
            </Stack>
            <Button size="xs" variant="subtle" onClick={() => navigate('/gateways/numbers')}>{t('gateways.preview.viewAll')}</Button>
          </Group>
          {attention.length === 0 && loaded
            ? <EmptyState title={t('gateways.preview.emptyTitle')} description={t('gateways.preview.empty')} />
            : (
              <DataTable<Indexed<PhoneNumber>>
                records={attention as Indexed<PhoneNumber>[]}
                loading={numbersQ.isLoading}
                idAccessor="id"
                emptyState={t('gateways.numbers.emptyRows')}
                onRowClick={() => navigate('/gateways/numbers')}
                columns={[
                  {
                    accessor: 'msisdn',
                    title: t('gateways.numbers.columns.displayName'),
                    render: (r) => (
                      <TwoLineCell
                        primary={r.msisdn ?? t('gateways.numbers.unnamed')}
                        secondary={r.display_name ?? undefined}
                      />
                    )
                  },
                  {
                    accessor: 'organisation_id',
                    title: t('gateways.numbers.columns.tenant'),
                    render: (r) => <TenantName id={r.organisation_id} />
                  },
                  {
                    accessor: 'quality_rating',
                    title: t('gateways.numbers.columns.quality'),
                    render: (r) => <StatusBadge status={qualityTone(r.quality_rating)} size="xs">{r.quality_rating}</StatusBadge>
                  },
                  {
                    accessor: 'is_active',
                    title: t('common.status'),
                    render: (r) => (
                      <StatusBadge status={r.is_active ? 'healthy' : 'inactive'} size="xs">
                        {r.is_active ? t('status.active') : t('status.disabled')}
                      </StatusBadge>
                    )
                  },
                  {
                    accessor: 'registered_at',
                    title: t('gateways.numbers.columns.registered'),
                    render: (r) => r.registered_at
                      ? <span title={formatDateTime(r.registered_at)}>{formatRelative(r.registered_at)}</span>
                      : <Text role="caption" c="var(--rr-color-text-muted)">{t('gateways.numbers.neverRegistered')}</Text>
                  }
                ]}
              />
            )}
        </Stack>
      </Card>
    </Stack>
  );
}

function ChannelCard({ title, description, workstream, footer, actions }: {
  title: string;
  description: string;
  workstream: string;
  footer: ReactNode;
  actions: ReactNode;
}) {
  const t = useT();
  return (
    <Card withBorder>
      <Stack gap="md" style={{ height: '100%' }}>
        <Group justify="space-between" align="flex-start">
          <Text role="subtitle" fw={600}>{title}</Text>
          {/* W-numbers are internal delivery references, so they carry a
              legend rather than standing alone as jargon. */}
          <Tooltip label={t('gateways.workstreamTooltip', { workstream })} position="top">
            <span><Badge size="xs" variant="light" color="orange">{workstream}</Badge></span>
          </Tooltip>
        </Group>

        <Text role="bodySmall" c="var(--rr-color-text-muted)" style={{ flex: 1 }}>
          {description}
        </Text>

        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          {footer}
          {actions}
        </Group>
      </Stack>
    </Card>
  );
}
